import React, { useEffect, useState } from 'react';
import { api } from '~/api';
import { useDeepCompareEffect, useToggle } from 'react-use';
import { useStoreState } from 'easy-peasy';
import R from 'ramda';
import { Loading, Select } from 'Components/nui';
import DropCheck from 'Components/nui/form/DropCheck';
import { useFilterTIData, useSharedFilter, useSharedTIData } from '..';
import moment from 'moment-timezone';

export default () => {
  const filterKeys = ['currency', 'uom', 'type', 'product'];

  const [range] = useSharedFilter();
  const [filterNames, setFilterNames] = useFilterTIData();
  const [filter, setFilter] = useState({});
  const [data, setData] = useSharedTIData();
  const [options, setOptions] = useState({
    currency: [],
    uom: [],
    product: [],
    type: [],
  });
  const apiError = data === 'Error';

  const solution = useStoreState(state => state.auth.solution);
  const [loading, toggle] = useToggle(true);
  const [availableOptions, setAvailableOptions] = useState({});
  const [products, setProducts] = useState([]);

  const periodStart = R.hasPath(['period', 'start'], data)
    ? moment(R.path(['period', 'start'], data))
    : undefined;
  const periodEnd = moment(
    R.pathOr(moment().format('YYYY-MM-DD'), ['period', 'end'], data)
  );
  const periodValidity = R.path(['period', 'validity'], data);

  useEffect(() => {
    let mounted = true;
    if (!range) return;

    (async () => {
      try {
        const data = await api.analytics('trade', [
          solution.id,
          range[0].format('YYYY-MM-DD'),
          range[1].format('YYYY-MM-DD'),
        ]);
        if (mounted) {
          const newData = R.pathOr({}, ['data', 'trade'], data);

          let newFilter = {};
          filterKeys.forEach(key => {
            const val = R.path(['filters', key], newData);
            if (val) {
              newFilter[key] = val;
            }
          });
          setFilter(newFilter);

          let newOptions = {};
          filterKeys.forEach(key => {
            newOptions[key] = R.pathOr([], ['fields', key, 'values'], newData);
          });
          setOptions(newOptions);
        }
      } catch (error) {
        // pass
      }
    })();

    return () => {
      mounted = false;
    };
  }, [solution, range, setFilter, setOptions]);

  useEffect(() => {
    const _new = {
      currency: R.propOr(
        '',
        'label',
        R.find(R.propEq('key', filter.currency), R.prop('currency', options))
      ),
      uom: R.propOr(
        '',
        'label',
        R.find(R.propEq('key', filter.uom), R.prop('uom', options))
      ),
      type: R.propOr(
        '',
        'label',
        R.find(R.propEq('key', filter.type), R.prop('type', options))
      ),
    };
    setFilterNames(_new);
  }, [filter, setFilterNames, options]);

  useEffect(() => {
    let mounted = true;
    toggle(true);
    if (!range) return;

    (async () => {
      try {
        const data = await api.analytics(
          'trade',
          [
            solution.id,
            range[0].format('YYYY-MM-DD'),
            range[1].format('YYYY-MM-DD'),
          ],
          { ...filter, product: products }
        );
        if (mounted) {
          const newData = R.pathOr({}, ['data', 'trade'], data);
          setData(newData);
          toggle(false);
        }
      } catch (error) {
        setData('Error');
        // pass
      }
    })();

    return () => {
      mounted = false;
    };
  }, [solution, range, setData, toggle, filter, products]);

  useDeepCompareEffect(() => {
    const newOptions = R.propOr(
      {},
      'options',
      R.clone(
        R.find(R.propEq('key', filter.currency), R.prop('currency', options))
      )
    );

    const newOptionsUom = R.propOr(
      {},
      'options',
      R.clone(R.find(R.propEq('key', filter.uom), R.prop('uom', options)))
    );

    newOptions.product = R.intersection(
      R.propOr([], 'product', newOptions),
      R.propOr([], 'product', newOptionsUom)
    );
    newOptions.type = R.intersection(
      R.propOr([], 'type', newOptions),
      R.propOr([], 'type', newOptionsUom)
    );

    if (filter.type) {
      const newOptionsType = R.propOr(
        {},
        'options',
        R.clone(R.find(R.propEq('key', filter.type), R.prop('type', options)))
      );

      newOptions.product = R.intersection(
        R.propOr([], 'product', newOptions),
        R.propOr([], 'product', newOptionsType)
      );
    }
    setAvailableOptions(newOptions);
  }, [filter, options, setAvailableOptions]);

  useDeepCompareEffect(() => {
    setProducts(R.propOr([], 'product', availableOptions));
  }, [availableOptions, setProducts]);

  const onCurrencyChange = value => {
    setFilter(state => ({ ...state, currency: value }));
  };

  return (
    !apiError &&
    (loading && R.isEmpty(filter) ? (
      <Loading size="small" />
    ) : (
      !R.isEmpty(data) && (
        <>
          {options.currency.length > 1 && (
            <div className="nui-fieldset currency-filter">
              <label htmlFor="currency">Currency</label>
              <div className="select-holder">
                <Select
                  id="currency"
                  value={filter.currency}
                  onChange={onCurrencyChange}
                >
                  {options.currency.map(item => (
                    <option value={item.key} key={item.key}>
                      {item.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          )}

          {options.uom.length > 1 && (
            <div className="nui-field-holder nui-fieldset unit-filter">
              <label htmlFor="unit">Unit</label>
              <div className="select-holder">
                <Select
                  id="unit"
                  value={filter.uom}
                  onChange={value =>
                    setFilter(state => ({ ...state, uom: value }))
                  }
                >
                  {options.uom.map(item => (
                    <option
                      value={item.key}
                      key={item.key}
                      disabled={
                        !R.propOr([], 'uom', availableOptions).includes(
                          item.key
                        )
                      }
                    >
                      {item.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          )}

          {options.type.length > 0 && (
            <div className="nui-field-holder nui-fieldset tradetype-filter">
              <label>Trade type</label>
              <div className="select-holder">
                <Select
                  value={filter.type}
                  onChange={value =>
                    setFilter(state =>
                      value
                        ? { ...state, type: value }
                        : R.dissoc('type', state)
                    )
                  }
                >
                  <option value="">Any</option>
                  {options.type.map(item => (
                    <option
                      value={item.key}
                      key={item.key}
                      disabled={
                        !R.propOr([], 'type', availableOptions).includes(
                          item.key
                        )
                      }
                    >
                      {item.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
          )}

          {options.product.length > 0 && (
            <div className="nui-field-holder products-filter">
              <label htmlFor="products">Products</label>

              <DropCheck
                name="products"
                title="All"
                enabledKeys={R.propOr([], 'product', availableOptions)}
                options={options.product.map(item => [item.key, item.label])}
                value={products}
                onChange={(_, values) => {
                  setProducts(values);
                }}
              />
            </div>
          )}

          {!loading && (
            <p className="filter-message p-0">
              Showing{' '}
              {products.length === 0 ||
              R.propOr([], 'product', availableOptions).length ===
                products.length ? (
                'all available products '
              ) : (
                <>
                  {products.length} product{products.length > 1 && 's'} out of{' '}
                  {R.propOr([], 'product', availableOptions).length}{' '}
                </>
              )}
              {filter.currency && (
                <>
                  in <strong>{filterNames.currency}</strong>{' '}
                </>
              )}
              {filter.uom && (
                <>
                  in <strong>{filterNames.uom}</strong>{' '}
                </>
              )}
              {filter.type && (
                <>
                  traded on <strong>{filterNames.type}</strong>{' '}
                </>
              )}
              {range && periodStart && (
                <>
                  for{' '}
                  <strong>
                    {periodStart.format('ll')}
                    {periodStart.format('ll') !== periodEnd.format('ll') && (
                      <> &mdash; {periodEnd.format('ll')}</>
                    )}
                  </strong>{' '}
                  {periodValidity && (
                    <span>
                      (Your date range has been limited to{' '}
                      {periodStart.format('ll')} due to a restriction on your
                      account (max period = {periodValidity}))
                    </span>
                  )}
                </>
              )}
            </p>
          )}
        </>
      )
    ))
  );
};
